/**
 * layout — the department page's binding of the shared detail-page surface.
 *
 * A department is a team with a different noun, and its detail page was a
 * near-verbatim copy of the team's — so it gets the same three panes, from
 * the same components (`Teams/surface/OrgContextPane`, the shared
 * `AccessPane`, `AuditLogPanel`):
 *
 *   context   status, counts, when it was created, its practice areas   (left)
 *   access    the members list, and the one way into changing it        (right)
 *   activity  the audit log                                            (right)
 *   reader    a document, read without leaving the page      (right, no rail)
 *
 * SEPARATE SURFACE, SHARED PANES. The pane BODIES are shared; the surface is
 * not, and must not be. `name: 'department'` is both the storage segment and
 * the `data-testid` prefix, so a department's arrangement is stored under its
 * own key and never inherits the one a team was left in.
 *
 * The rest of the reasoning — entity scope, no legacy keys, the reader pane
 * that replaced the `attachmentViewer` modal, no detail pane — is identical to
 * the team's; see `Teams/surface/layout`.
 */

import {
  PanelBounds,
  PanelLayout as GenericLayout,
  PanelSlot as GenericSlot,
} from '../../../shared/surface/panelLayout';
import defineDetailSurface, {
  DetailPaneConfig,
} from '../../../shared/surface/defineDetailSurface';

export type DepartmentPanelId = 'context' | 'access' | 'activity' | 'reader';

/** The canvas's tabs. Labels are the page's — see `DepartmentDetails`. */
export type DepartmentTab = 'Details' | 'Feed' | 'Documents';

export const DEPARTMENT_TABS: DepartmentTab[] = ['Details', 'Feed', 'Documents'];

export type DepartmentPanelSlot = GenericSlot<DepartmentPanelId>;
export type DepartmentPanelLayout = GenericLayout<DepartmentPanelId>;

export const DEPARTMENT_PANEL_IDS: DepartmentPanelId[] = [
  'context', 'access', 'activity', 'reader',
];

const DEPARTMENT_PANES: Record<DepartmentPanelId, DetailPaneConfig> = {
  context: {
    label: 'Context',
    bounds: { min: 260, max: 720 },
    dock: 'left', index: 0, width: 320, visible: true, rail: true,
  },
  access: {
    label: 'Members',
    bounds: { min: 260, max: 640 },
    dock: 'right', index: 0, width: 320, visible: false, rail: true,
  },
  activity: {
    label: 'Activity',
    bounds: { min: 280, max: 760 },
    dock: 'right', index: 1, width: 340, visible: false, rail: true,
  },
  /** No rail pill: it opens on a document, never on nothing. */
  reader: {
    label: 'Reader',
    bounds: { min: 320, max: 900 },
    dock: 'right', index: 2, width: 560, visible: false,
  },
};

export const DEPARTMENT_SURFACE = defineDetailSurface<DepartmentPanelId, DepartmentTab>({
  kind: 'detail',
  name: 'department',
  scope: 'entity',
  ids: DEPARTMENT_PANEL_IDS,
  panes: DEPARTMENT_PANES,
  tabs: DEPARTMENT_TABS,
  readerPane: 'reader',
});

/* ── derived, for call sites and tests that want the plain records ── */

export const DEPARTMENT_PANEL_LABELS: Record<DepartmentPanelId, string> = DEPARTMENT_SURFACE.labels;
export const DEPARTMENT_PANEL_BOUNDS: Record<DepartmentPanelId, PanelBounds> = DEPARTMENT_SURFACE.bounds;
export const DEPARTMENT_DEFAULT_LAYOUT: DepartmentPanelLayout = DEPARTMENT_SURFACE.defaults;
